// @ts-nocheck
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { BackgroundImage } from '@mantine/core';
import { useMantineTheme, useMantineColorScheme } from '@mantine/core';
import { Anchor, Badge, Box, Button, Center, Checkbox, Container, Grid, Group, PasswordInput, Stack, Text, TextInput, Title } from '@/components/ui';
import { IconMail, IconLock, IconArrowLeft, IconPhoto } from '@tabler/icons-react';
import { FadeIn, SlideIn } from '@/components/animations';
import { AuthIllustration, SocialLoginButtons } from '../components';

export default function LoginIllustrated() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const theme = useMantineTheme();
  const { colorScheme } = useMantineColorScheme();
  const isDark = colorScheme === 'dark';

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    console.log('Login attempt:', { email, password, rememberMe });
  };

  return (
    <Box style={{ minHeight: '100vh', display: 'flex' }}>
      {/* Left side - Illustration & Image */}
      <Box
        style={{
          width: '55%',
          display: 'none',
          position: 'relative',
          overflow: 'hidden',
        }}
        visibleFrom="md"
      >
        {/* Stock image background */}
        <BackgroundImage
          src="https://images.unsplash.com/photo-1497366216548-37526070297c?w=1200&q=80"
          style={{
            position: 'absolute',
            inset: 0,
          }}
        />

        {/* Overlay */}
        <Box
          style={{
            position: 'absolute',
            inset: 0,
            background: `linear-gradient(135deg, ${theme.colors.indigo[9]}dd, ${theme.colors.cyan[9]}cc)`,
          }}
        />

        {/* Content */}
        <Center h="100%" p="xl" style={{ position: 'relative', zIndex: 1 }}>
          <Stack align="center" gap="xl">
            <SlideIn direction="down" delay={200}>
              <AuthIllustration type="login" size={250} animated />
            </SlideIn>

            <FadeIn delay={400}>
              <Stack align="center" gap="sm">
                <Title order={2} c="white" ta="center">
                  Secure Access
                </Title>
                <Text c="white" ta="center" opacity={0.9} maw={350}>
                  Your data is protected with enterprise-grade security.
                  Sign in to access your personalized dashboard.
                </Text>
              </Stack>
            </FadeIn>

            {/* Feature highlights */}
            <FadeIn delay={600}>
              <Group gap="xl" mt="lg">
                {[
                  { icon: '🔒', label: 'Encrypted' },
                  { icon: '⚡', label: 'Fast' },
                  { icon: '🛡️', label: 'Protected' },
                ].map((item) => (
                  <Stack key={item.label} align="center" gap={4}>
                    <Text size="xl">{item.icon}</Text>
                    <Text size="sm" c="white" opacity={0.8}>{item.label}</Text>
                  </Stack>
                ))}
              </Group>
            </FadeIn>
          </Stack>
        </Center>
      </Box>

      {/* Right side - Form */}
      <Box
        style={{
          flex: 1,
          backgroundColor: isDark ? theme.colors.dark[7] : theme.colors.white,
        }}
      >
        <Center mih="100vh" p="xl">
          <Container size="xs" w="100%">
            <Stack gap="lg">
              {/* Back button */}
              <Button
                component={Link}
                to="/demo/auth-pages/login"
                variant="subtle"
                size="xs"
                leftSection={<IconArrowLeft size={14} />}
                w="fit-content"
              >
                Back to variants
              </Button>

              {/* Header */}
              <Stack align="center" gap="xs">
                <Badge
                  leftSection={<IconPhoto size={12} />}
                  variant="gradient"
                  gradient={{ from: 'cyan', to: 'teal' }}
                  size="lg"
                >
                  Illustrated Design
                </Badge>
                <Title order={2} ta="center" mt="md">
                  Welcome back
                </Title>
                <Text c="dimmed" size="sm" ta="center">
                  Sign in with your credentials
                </Text>
              </Stack>

              {/* Mobile illustration */}
              <Center hiddenFrom="md">
                <AuthIllustration type="login" size={120} animated />
              </Center>

              {/* Form */}
              <form onSubmit={handleSubmit}>
                <Stack gap="md">
                  <TextInput
                    label="Email"
                    placeholder="your@email.com"
                    leftSection={<IconMail size={16} />}
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    size="md"
                    radius="md"
                  />

                  <PasswordInput
                    label="Password"
                    placeholder="Your password"
                    leftSection={<IconLock size={16} />}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    size="md"
                    radius="md"
                  />

                  <Group justify="space-between">
                    <Checkbox
                      label="Remember me"
                      checked={rememberMe}
                      onChange={(e) => setRememberMe(e.currentTarget.checked)}
                    />
                    <Anchor
                      component={Link}
                      to="/demo/auth-pages/forgot-password/illustrated"
                      size="sm"
                    >
                      Forgot password?
                    </Anchor>
                  </Group>

                  <Button
                    type="submit"
                    fullWidth
                    size="md"
                    radius="md"
                    variant="gradient"
                    gradient={{ from: 'cyan', to: 'teal' }}
                  >
                    Sign in
                  </Button>
                </Stack>
              </form>

              {/* Social Login */}
              <SocialLoginButtons
                providers={['google', 'github', 'apple']}
                layout="icons-only"
                dividerLabel="or continue with"
              />

              {/* Register Link */}
              <Text ta="center" size="sm">
                Don't have an account?{' '}
                <Anchor component={Link} to="/demo/auth-pages/register/illustrated" fw={500}>
                  Sign up
                </Anchor>
              </Text>
            </Stack>
          </Container>
        </Center>
      </Box>
    </Box>
  );
}
